import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Music, Heart, Wifi, WifiOff } from 'lucide-react';
import { PageHeader, PageHero, PageFooter } from '../components/PageLayout';
import useSEO from '../hooks/useSEO';

const AboutPage = () => {
  const navigate = useNavigate();
  
  // SEO Meta Tags
  useSEO({
    title: 'À Propos - Notre Histoire | DJ Nomad',
    description: "Découvrez l'histoire de DJ Nomad : comment une soirée entre amis a donné naissance à la première application DJ web offline-first.",
    canonical: 'https://djnomad.app/about',
    ogTitle: 'À Propos de DJ Nomad'
  });

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <PageHeader />

      {/* Hero */}
      <PageHero 
        icon={Heart}
        title="Notre Histoire"
        subtitle="Comment une soirée entre amis a donné naissance à la première application DJ web offline-first"
      />

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12 flex-1">
        {/* Story Section */}
        <div className="space-y-8">
          {/* Chapter 1 */}
          <div className="bg-white border border-gray-200 rounded-2xl p-6 md:p-8 shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-violet-600 to-purple-600 rounded-xl flex items-center justify-center shadow-md">
                <Heart className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900">Été 2023 : Le Mariage de Lucas</h2>
            </div>
            <div className="space-y-4 text-gray-600 leading-relaxed">
              <p>
                Tout a commencé lors du mariage de Lucas, notre meilleur ami. Le lieu ? Une magnifique cave voûtée du XVIIe siècle en pleine campagne. Le problème ? <span className="text-violet-600 font-semibold">Aucun réseau mobile, pas de WiFi</span>.
              </p>
              <p>
                Lucas nous avait demandé de gérer la musique. Aucun de nous n'était DJ professionnel, mais on aimait la musique et on voulait lui offrir une soirée inoubliable. On avait tout préparé : playlists Spotify, transitions, ambiances par moments de la soirée...
              </p>
              <p className="text-gray-900 font-semibold">
                Le jour J : catastrophe. Spotify ne chargeait rien. Les applications DJ web classiques refusaient de fonctionner sans connexion. La panique.
              </p>
            </div>
          </div>

          {/* Chapter 2 */}
          <div className="bg-white border border-gray-200 rounded-2xl p-6 md:p-8 shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-600 to-violet-600 rounded-xl flex items-center justify-center shadow-md">
                <WifiOff className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900">Le Déclic : Et si...</h2>
            </div>
            <div className="space-y-4 text-gray-600 leading-relaxed">
              <p>
                Heureusement, on avait téléchargé quelques MP3 en backup sur un ordinateur portable. On a bidouillé une solution avec Audacity et VLC, mais c'était loin d'être professionnel. Pas de crossfade, pas de gestion de playlists, pas de fluidité.
              </p>
              <p>
                <span className="text-violet-600 font-semibold">Cette nuit-là, entre deux morceaux, l'idée nous est venue</span> : pourquoi n'existe-t-il pas une application DJ moderne qui fonctionne <span className="italic">vraiment</span> sans connexion Internet ?
              </p>
              <div className="bg-violet-50 border border-violet-200 rounded-xl p-4 italic text-violet-700">
                "On dépend trop d'Internet. Et si on créait une app qui prépare tout en ligne, mais qui marche 100% offline quand on en a besoin ?"
              </div>
            </div>
          </div>

          {/* Chapter 3 */}
          <div className="bg-white border border-gray-200 rounded-2xl p-6 md:p-8 shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl flex items-center justify-center shadow-md">
                <Wifi className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900">La Naissance de DJ Nomad</h2>
            </div>
            <div className="space-y-4 text-gray-600 leading-relaxed">
              <p>
                Les mois suivants, on a travaillé sur DJ Nomad. Notre obsession : <span className="text-emerald-600 font-semibold">créer l'application DJ web la plus fiable au monde</span>, celle qui ne vous lâche jamais, même sans réseau.
              </p>
              <p>
                On a intégré les technologies PWA (Progressive Web App) pour permettre le téléchargement complet d'événements. On a ajouté un dual-deck professionnel avec crossfade de qualité studio. On a pensé à tout : détection BPM, beatmatching, gestion d'événements par parties, QR codes pour les invités...
              </p>
              <p className="text-gray-900 font-semibold">
                Mais surtout, on a fait en sorte que vous puissiez préparer votre soirée tranquillement chez vous avec Internet, puis partir serein : tout fonctionne offline, automatiquement.
              </p>
            </div>
          </div>

          {/* Mission & Vision */}
          <div className="bg-gradient-to-br from-violet-50 to-purple-50 border border-violet-200 rounded-2xl p-6 md:p-8">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 text-center">Notre Mission</h2>
            <div className="space-y-6">
              <div className="text-center">
                <p className="text-xl md:text-2xl text-violet-600 font-bold mb-4">
                  "Démocratiser le DJing et rendre l'animation d'événements accessible à tous"
                </p>
                <p className="text-gray-600 leading-relaxed">
                  Vous n'avez pas besoin d'être un DJ professionnel. Vous n'avez pas besoin de matériel coûteux. Vous n'avez même pas besoin d'Internet en permanence.
                </p>
              </div>
              <div className="grid md:grid-cols-3 gap-6 mt-8">
                <div className="text-center bg-white p-4 rounded-xl border border-gray-200">
                  <div className="text-4xl mb-3">🎉</div>
                  <h3 className="text-gray-900 font-semibold mb-2">Pour Tous</h3>
                  <p className="text-gray-500 text-sm">Mariages, anniversaires, soirées entre amis</p>
                </div>
                <div className="text-center bg-white p-4 rounded-xl border border-gray-200">
                  <div className="text-4xl mb-3">💪</div>
                  <h3 className="text-gray-900 font-semibold mb-2">Sans Stress</h3>
                  <p className="text-gray-500 text-sm">Préparez online, mixez offline, sync auto</p>
                </div>
                <div className="text-center bg-white p-4 rounded-xl border border-gray-200">
                  <div className="text-4xl mb-3">🌍</div>
                  <h3 className="text-gray-900 font-semibold mb-2">Partout</h3>
                  <p className="text-gray-500 text-sm">Cave, plage, montagne, festival</p>
                </div>
              </div>
            </div>
          </div>

          {/* Values */}
          <div className="space-y-6">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 text-center mb-8">Nos Valeurs</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                <h3 className="text-lg font-bold text-gray-900 mb-3">🔒 Fiabilité Absolue</h3>
                <p className="text-gray-600">
                  Votre événement est trop important pour dépendre d'un réseau instable. DJ Nomad fonctionne, point.
                </p>
              </div>
              <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                <h3 className="text-lg font-bold text-gray-900 mb-3">💝 Gratuit & Accessible</h3>
                <p className="text-gray-600">
                  Pas de carte bancaire, pas d'abonnement caché. La musique doit être accessible à tous.
                </p>
              </div>
              <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                <h3 className="text-lg font-bold text-gray-900 mb-3">🎨 Qualité Professionnelle</h3>
                <p className="text-gray-600">
                  Crossfade sans coupures, BPM sync, beatmatching. Vous méritez du matériel de pro.
                </p>
              </div>
              <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                <h3 className="text-lg font-bold text-gray-900 mb-3">🚀 Innovation Continue</h3>
                <p className="text-gray-600">
                  On ne s'arrête jamais. Chaque feedback compte, chaque idée est étudiée.
                </p>
              </div>
            </div>
          </div>

          {/* Team Story */}
          <div className="bg-white border border-gray-200 rounded-2xl p-6 md:p-8 shadow-sm">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 text-center">L'Équipe</h2>
            <p className="text-gray-600 leading-relaxed text-center max-w-2xl mx-auto">
              Nous sommes une petite équipe de passionnés de musique et de technologie. On n'est pas des DJs pros, juste des gens qui adorent animer des soirées et qui en avaient marre des galères techniques.
            </p>
            <p className="text-violet-600 font-semibold text-center mt-6 text-lg">
              DJ Nomad, c'est notre cadeau à la communauté. Fait avec ❤️ pour les DJs du dimanche comme nous.
            </p>
          </div>

          {/* CTA */}
          <div className="text-center pt-8">
            <button
              onClick={() => navigate('/login')}
              className="px-8 py-4 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white text-lg font-bold rounded-xl shadow-lg transition-all duration-300 transform hover:scale-105 inline-flex items-center gap-2"
            >
              Rejoignez l'Aventure
              <Music className="w-5 h-5" />
            </button>
            <p className="text-gray-500 mt-4">
              Gratuit • Sans engagement • Prêt en 2 minutes
            </p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <PageFooter />
    </div>
  );
};

export default AboutPage;
